import { cloneElement, isValidElement, useId, type ReactElement, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Label } from './input';

/**
 * Nhóm nhãn + ô nhập + lỗi/gợi ý. Tự gắn <label for> với ô nhập con (nếu con là một phần tử đơn)
 * và aria-describedby cho thông báo lỗi/gợi ý — hỗ trợ trình đọc màn hình.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
  className,
}: {
  label?: ReactNode;
  htmlFor?: string;
  error?: string;
  hint?: ReactNode;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const auto = useId();
  const single =
    isValidElement(children) && typeof children.type !== 'string'
      ? true
      : isValidElement(children) && ['input', 'select', 'textarea'].includes(children.type as string);
  const childId = isValidElement(children)
    ? ((children.props as { id?: string }).id ?? undefined)
    : undefined;
  const id = htmlFor ?? childId ?? (single ? auto : undefined);
  const descId = error || hint ? `${auto}-desc` : undefined;
  const content =
    single && isValidElement(children) && !childId
      ? cloneElement(children as ReactElement<Record<string, unknown>>, {
          id,
          'aria-describedby': descId,
          ...(error ? { 'aria-invalid': true } : {}),
        })
      : children;
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </Label>
      )}
      {content}
      {error ? (
        <p id={descId} className="text-xs text-red-600">
          {error}
        </p>
      ) : hint ? (
        <p id={descId} className="text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
